import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GeofenceService } from '@app-asset-config/services/geofence.service';
import { GeofenceSettingsListingComponent } from '../geofence-settings-listing/geofence-settings-listing.component';
import { GeofenceSetting } from '@app-asset-config/models/geofence.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { dirtyCheck } from '@app-core/models/dirty-check';
import * as Papa from 'papaparse';
import * as toGeoJSON from 'togeojson';
import { GEOFENCE_MIN_POLYGON_POINT } from '@app-core/constants/constants';

@Component({
  selector: 'app-geofence-bulk-import',
  templateUrl: './geofence-bulk-import.component.html',
  styleUrls: ['./geofence-bulk-import.component.scss'],
})
export class GeofenceBulkImportComponent implements OnInit {
  public setting: GeofenceSetting = { propertyId: -1, propertyName: 'Select Geofence Settings' } as any;

  public fileUploadStatus: 'pending' | 'uploaded' | 'error' = 'pending';

  public bulkGeofenceForm = new FormGroup({
    geofences: new FormControl(null, Validators.required),
    propertyId: new FormControl(null, Validators.required),
  });

  public isDirty$ = of(false);
  public unSubscribe = new Subject<void>();

  public bulkGeofenceData: { invalid: any[]; valid: any[] } = { invalid: [], valid: [] };

  constructor(public geofenceService: GeofenceService, private translateService: TranslateService) {
    this.setting.propertyName = this.translateService.instant('geofenceBulkImportSelectSettings');
  }

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.isDirty$ = this.bulkGeofenceForm.valueChanges.pipe(takeUntil(this.unSubscribe), dirtyCheck(of(this.bulkGeofenceForm.value)));
  }

  openGeofenceSettingSelection() {
    this.geofenceService.navigateTo(GeofenceSettingsListingComponent, {
      mode: 'select',
      from: 'filter',
      'output:selected': (setting: GeofenceSetting) => {
        this.setting = { ...setting }; // setting for UI
        this.bulkGeofenceForm.patchValue({ propertyId: setting.propertyId });
        setTimeout(() => this.bulkGeofenceForm.updateValueAndValidity(), 50);
      },
    });
  }

  downloadSampleFile(filename: string) {
    const link = document.createElement('a');
    link.href = `assets/common/geofence/${filename}`;
    link.download = filename;
    link.click();
  }

  async uploadFile(event) {
    const file = event.target.files[0];

    // extracting extensions
    const parts = file.name.split('.');

    const ext = parts.length > 1 ? parts.pop() : null;

    this.clearFileInput();

    try {
      switch (ext) {
        case 'csv':
          this.bulkGeofenceData = await this.parseCSV(file);
          break;
        case 'geojson':
          this.bulkGeofenceData = await this.parseGeoJSON(file);
          break;
        case 'kml':
          this.bulkGeofenceData = await this.parseKML(file);
          break;
        default:
          return this.geofenceService.showToasts(this.translateService.instant('geofenceToastUnsupportedFileType'), 'error');
      }

      const { valid, invalid } = this.bulkGeofenceData;

      if (invalid.length > 0) {
        // TODO notify invalid entries
      }

      if (valid.length > 0) {
        // TODO set valid count
        this.bulkGeofenceForm.patchValue({ geofences: [...valid] });
        this.fileUploadStatus = 'uploaded';

        setTimeout(() => this.bulkGeofenceForm.updateValueAndValidity(), 50);
      }
    } catch (err) {
      console.log(err);
      this.geofenceService.showToasts(this.translateService.instant('geofenceToastErrorProcessingFile'), 'error');
      this.fileUploadStatus = 'error';
      this.bulkGeofenceForm.patchValue({ geofences: null });
      this.clearFileInput();
    }
  }

  // CSV parsing
  parseCSV(file: File): any {
    return new Promise((res, rej) => {
      Papa.parse(file, {
        header: true, // if you want headers to be parsed
        skipEmptyLines: true,
        error: (err) => {
          console.error('Error parsing CSV:', err);
          rej(err);
        },
        complete: (result) => {
          const respObj = {
            valid: [],
            invalid: [],
          };

          // grouping coordinates based on type and geofence name
          const geofenceNameObjectMapping = {};

          result.data.forEach((row) => {
            let {
              Address: address,
              'Geofence Name': geofenceName,
              Latitude: latitude,
              Longitude: longitude,
              Radius: radius,
              'Shape Type (circle/polygon)': shapeType,
              'Unit (km/ml)': unit,
            } = row;

            // initing
            if (!geofenceNameObjectMapping[geofenceName]) {
              const obj = {
                geofenceName,
                polygonCoordinates: [],
                tagIds: [],
                address: { completeAddress: address },
              };

              geofenceNameObjectMapping[geofenceName] = obj;
            }

            // setting for circle type
            if (shapeType === 'circle') {
              radius = unit === 'km' ? radius * 1000 : radius * 1609.34;
              geofenceNameObjectMapping[geofenceName].polygonCoordinates = this.geofenceService
                .calculateCoordinatesForCircle(longitude, latitude, radius)
                .map((coord) => ({ latitude: coord[1], longitude: coord[0] }));
              return;
            }

            // pushing for polygon type
            if (shapeType === 'polygon') {
              geofenceNameObjectMapping[geofenceName].polygonCoordinates.push({
                longitude: typeof longitude === 'string' ? Number(longitude) : longitude,
                latitude: typeof latitude === 'string' ? Number(latitude) : latitude,
              });
            }
          });
          // verify whether atleast 3 point exists to make a polygon
          Object.keys(geofenceNameObjectMapping)
            .map((key) => geofenceNameObjectMapping[key])
            .forEach((geoFence) => {
              const { polygonCoordinates } = geoFence;

              if (polygonCoordinates.length < GEOFENCE_MIN_POLYGON_POINT) {
                return respObj.invalid.push(geoFence);
              }

              // verify whether first and last point are same for geofence to form a polygon
              const firstPoint = polygonCoordinates[0];
              const lastPoint = polygonCoordinates[polygonCoordinates.length - 1];

              if (firstPoint.longitude !== lastPoint.longitude || firstPoint.latitude !== lastPoint.latitude) {
                geoFence.polygonCoordinates.push({ ...firstPoint });
              }

              return respObj.valid.push(geoFence);
            });

          res(respObj);
        },
      });
    });
  }

  // GeoJSON parsing
  parseGeoJSON(file: File): any {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const geoJson = JSON.parse(reader.result as string);

          // create the intended object
          if (!geoJson.features || !Array.isArray(geoJson.features)) {
            throw new Error('Invalid geojson structure');
          }

          const respObj = geoJson.features.reduce(
            (acc, feature) => {
              const isvalid = this.validateGeoJSONFeature(feature);
              if (!isvalid) {
                acc.invalid.push(feature);
                return acc;
              }

              const { name, address } = feature.properties;
              const { coordinates } = feature.geometry;

              const obj = {
                geofenceName: name,
                polygonCoordinates: coordinates.map((coord) => ({ longitude: coord[0], latitude: coord[1] })),
                tagIds: [],
                address: { completeAddress: address },
              };

              acc.valid.push(obj);

              return acc;
            },
            { valid: [], invalid: [] }
          );

          res(respObj);
        } catch (err) {
          rej(err);
        }
      };
      reader.readAsText(file);
    });
  }

  parseKML(file: File): any {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => {
        const kmlData = reader.result as string;
        const parser = new DOMParser();

        try {
          const kmlDoc = parser.parseFromString(kmlData, 'text/xml');

          const geoJson = toGeoJSON.kml(kmlDoc);

          console.log(geoJson);

          // create the intended object
          if (!geoJson.features || !Array.isArray(geoJson.features)) {
            throw new Error('Invalid geojson structure');
          }

          const respObj = geoJson.features.reduce(
            (acc, feature) => {
              const isvalid = this.validateGeoJSONFeature(feature, true);
              if (!isvalid) {
                acc.invalid.push(feature);
                return acc;
              }

              const { name, address } = feature.properties;
              const { coordinates } = feature.geometry;

              const obj = {
                geofenceName: name,
                polygonCoordinates: coordinates[0].map((coord) => ({ longitude: coord[0], latitude: coord[1] })),
                tagIds: [],
                address: { completeAddress: address },
              };

              acc.valid.push(obj);

              return acc;
            },
            { valid: [], invalid: [] }
          );

          res(respObj);
        } catch (err) {
          rej(err);
        }
      };
      reader.readAsText(file);
    });
  }

  // HELPER function to validate geojson structure
  validateGeoJSONFeature(feature, fromKml = false) {
    // Basic structure
    if (typeof feature !== 'object' || feature === null) return false;
    if (feature.type !== 'Feature') return false;
    if (!feature.geometry || typeof feature.geometry !== 'object') return false;

    // Geometry checks
    const geom = feature.geometry;
    if (geom.type !== 'Polygon') return false;

    let coords = geom.coordinates;
    if (fromKml) {
      // KML to GeoJSON conversion may wrap coordinates differently
      coords = coords.length > 0 ? coords[0] : null;
    }

    if (!Array.isArray(coords) || coords.length < 2) return false;

    // Validate each coordinate tuple
    for (const point of coords) {
      if (!Array.isArray(point) || point.length < 2 || typeof point[0] !== 'number' || typeof point[1] !== 'number') {
        return false;
      }
    }

    // Polygon ring must be closed (first == last)
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1] || (first[2] || 0) !== (last[2] || 0)) {
      return false;
    }

    // Validate properties
    const props = feature.properties;
    if (!props || typeof props.name !== 'string' || typeof props?.address !== 'string') {
      return false;
    }

    return true;
  }

  clearFileInput() {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  bulkImportGeofence() {
    if (this.bulkGeofenceForm.invalid) {
      this.geofenceService.showToasts('Fill in required fields', 'error');
      return;
    }

    const body = {
      propertyId: this.bulkGeofenceForm.value.propertyId,
      geofences: this.bulkGeofenceForm.value.geofences,
    };

    this.geofenceService.createGeofence(body).subscribe(
      (res: any) => {
        // when no errors
        if (!res.errors.length) {
          this.geofenceService.fetchGeofence(true);
          this.geofenceService.showToasts(this.translateService.instant('geofenceToastGeofenceCreated'));

          this.geofenceService.commonDataBus.next({ event: 'SHOW_HOME' });
          this.geofenceService.pop({ clearEmbedded: true });
          return;
        }

        this.geofenceService.showToasts(this.translateService.instant('geofenceToastErrorCreatingGeofence'), 'error');
      },
      (err) => {
        console.log('Error occurred... : ', err);
        this.geofenceService.showToasts(this.translateService.instant('geofenceToastErrorCreatingGeofence'), 'error');
        // TODO handle partial success of creating geofence
      }
    );
  }
}
